/**
 * Near-Me Helpers
 * ===============
 * Shared Zod utilities for near-me schemas (geo, phone, url, time).
 */

import { z } from 'zod';

/**
 * Converts empty / whitespace-only strings to `undefined`
 * so Zod `.optional()` treats them as "not provided".
 */
export const emptyStringToUndefined = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }
  return value;
};

/** Optional trimmed string, empty coerced to `undefined`. `max` defaults to 200. */
export const optionalTrimmedString = (max = 200) =>
  z.preprocess(emptyStringToUndefined, z.string().trim().max(max).optional());

/**
 * GeoJSON Point for location storage.
 * Coordinates: [longitude, latitude] (GeoJSON order).
 */
export const geoJsonPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: z.tuple([
    z.number().min(-180).max(180), // longitude
    z.number().min(-90).max(90), // latitude
  ]),
});
export type GeoJsonPoint = z.infer<typeof geoJsonPointSchema>;

/** Indian phone number: 10 digits (6-9 leading), optional +91 / 0 prefix. */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^(?:\+91[-\s]?|0)?[6-9]\d{9}$/, 'Enter a valid Indian phone number');

/** Optional phone (empty -> undefined). */
export const optionalPhoneSchema = z.preprocess(emptyStringToUndefined, phoneSchema.optional());

/** Optional URL (empty -> undefined). */
export const optionalUrlSchema = z.preprocess(
  emptyStringToUndefined,
  z.string().trim().url().max(300).optional(),
);

/** Optional email (empty -> undefined). */
export const optionalEmailSchema = z.preprocess(
  emptyStringToUndefined,
  z.string().trim().email().max(200).optional(),
);

/** 24-hour clock time string, "HH:MM". */
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM (24h) format');
